<template>
  <section class="login-page">
    <!-- Coluna do login -->
    <div class="panel">
      <header class="panel-head">
        <img
          class="panel-logo"
          :src="require('../../public/img/white_logo.png')"
          alt="QUARTIX Markets"
        />

        <!-- Seletor de idioma. Fica na barra do logo, no canto oposto: e o
             unico lugar do painel que ja escapa da faixa centralizada, entao o
             seletor ancora no padding direito sem disputar eixo com o
             formulario nem com o rodape.
             O mecanismo e o mesmo do menu Settings - os dois chamam setLocale()
             e leem a lista de AVAILABLE_LOCALES. -->
        <div class="lang-switch" role="group" :aria-label="$t('settingsModal.defaultLanguage')">
          <button
            v-for="loc in locales"
            :key="loc.value"
            type="button"
            class="lang-option"
            :class="{ 'is-active': $i18n.locale === loc.value }"
            :aria-pressed="String($i18n.locale === loc.value)"
            @click="trocarIdioma(loc.value)"
          >
            {{ loc.label }}
          </button>
        </div>
      </header>

      <div ref="corpo" class="panel-body" :class="{ 'is-compact': precisaEspaco }">
        <!-- As particulas da casa, ancoradas no centro deste bloco - o
             tamanho do disco e o tamanho do bloco. O host vem do index.html;
             ver adotarParticulas().
             Esta e a posicao dos passos de login: acima do bloco. No passo do
             "request a demo" o disco desce para baixo do titulo - quem o move
             de lugar e moverOrb(), e o elemento continua sendo este. -->
        <div
          ref="particles"
          class="panel-orb"
          :class="{ 'is-live': particulasVivas }"
        ></div>

        <carousel-modal-login @needs-room="ajustarEspaco" />
      </div>

      <!-- Os produtos saem de cena no passo do "request a demo": o formulario
           inteiro nao cabe no painel, e esta lista era justamente o que ficava
           por baixo dele. v-if e nao v-show de proposito - o que se quer e
           devolver a altura, nao esconder um bloco que continua ocupando lugar
           e desequilibrando o eixo vertical do que sobrou. -->
      <nav v-if="!precisaEspaco" class="panel-links">
        <landing-card
          v-for="(card, i) in cards"
          :key="card.id + i"
          :card="card"
          @learn-more="avisarSaibaMais"
        />
      </nav>

      <footer class="panel-foot">
        {{ $t("extras.footer") }}
      </footer>
    </div>
    <!-- Coluna da arte: a imagem sob um veu de alta opacidade, so textura. -->
    <div class="hero" :style="heroStyle">
      <div class="hero-art"></div>
      <div class="hero-veil"></div>

      <!-- Painel de calibragem: existe apenas para escolher os numeros do veu
           na tela real. Abra /login?tune, mexa nos tres controles e copie o
           bloco pronto para o <style> abaixo. -->
      <div v-if="tuning" class="hero-tuner">
        <div class="tuner-title">veil tuner</div>
        <div v-for="knob in knobs" :key="knob.key" class="tuner-row">
          <span class="tuner-label">{{ knob.label }}</span>
          <v-slider
            v-model="tune[knob.key]"
            :min="0"
            :max="knob.max"
            :step="0.01"
            hide-details
            dense
            color="primary"
            class="tuner-slider"
          />
          <span class="tuner-value">{{ tune[knob.key].toFixed(2) }}</span>
        </div>
        <pre class="tuner-snippet">{{ tuneSnippet }}</pre>
      </div>
    </div>

  </section>
</template>

<script>
import CarouselModalLogin from "@/components/modal/CarouselModalLogin.vue";
import LandingCard from "@/components/common/LandingCard.vue";
import { setLocale, AVAILABLE_LOCALES } from "@/i18n/i18n";
import { sendLearnMoreAlert } from "@/utils/learnMoreAlert";
import { peekVisitor } from "@/utils/visitor";
import { collectClientInfo } from "@/utils/clientInfo";

// Os dois diametros do disco, em px. Vivem aqui E no CSS (.panel-orb e
// .is-compact .panel-orb): quem posiciona e o CSS, mas o tamanho da particula
// depende da largura do canvas e por isso e acertado em JS - ver ajustarOrb().
// Mudar um lado sem o outro deixa a caixa e a nuvem em desacordo.
const ORB_PX = 190;
const ORB_PX_COMPACTO = 80;

// Quanto a escala da particula sobe no disco compacto.
//
// Compensacao CHEIA seria a razao dos diametros (190/80 = 2.4): manteria a
// particula do mesmo tamanho ABSOLUTO num disco 2,4x menor, ou seja, a nuvem
// ficaria visivelmente mais grossa - o oposto de encolher. Sem compensacao
// nenhuma (fator 1) a nuvem seria uma copia reduzida fiel, mas a particula cai
// para 42% e vira poeira, que e justamente o problema que fez o host nascer com
// data-particles-scale="2.2".
//
// A raiz da razao fica no meio dos dois: a particula ainda diminui (para ~65%
// do tamanho que tinha no disco grande), mas continua legivel. Sai dos dois
// diametros de proposito - encolher mais o disco afrouxa a compensacao junto,
// sem um terceiro numero para lembrar de acertar.
// Se a nuvem compacta ficar grossa ou rala demais, este e o ponto para mexer
// (um literal serve). Calibravel ao vivo no /login?tune, cujo slider
// `particles` escreve na cena e imprime o valor.
const ORB_FATOR_COMPACTO = Math.sqrt(ORB_PX / ORB_PX_COMPACTO);

export default {
  components: {
    CarouselModalLogin,
    LandingCard,
  },

  data() {
    return {
      // Segura a opacidade das particulas ate o primeiro frame existir: o
      // WebGL compila e linka os shaders no render inicial, e sem o fade esse
      // engasgo aparece como um piscar de canvas vazio.
      particulasVivas: false,
      // Ligado por ?tune na URL. Sem isso o componente nao emite style nenhum
      // e quem manda no veu e o CSS.
      tuning: Object.prototype.hasOwnProperty.call(this.$route.query, "tune"),
      tune: { veil: 0.6, sat: 0, bright: 1, scale: 2.2, drift: 0.25 },
      knobs: [
        { key: "veil", label: "veil", max: 1 },
        { key: "sat", label: "saturate", max: 1 },
        { key: "bright", label: "brightness", max: 1.5 },
        // Estes dois nao sao variaveis CSS: escrevem direto na cena (ver
        // watch), porque o modulo le particlesScale e drift a cada frame. Os
        // valores escolhidos viram atributos do host, no index.html.
        { key: "scale", label: "particles", max: 5 },
        { key: "drift", label: "drift", max: 1 },
      ],
      locales: AVAILABLE_LOCALES,
      // Ligado pelo passo do "request a demo" (evento do CarouselModalLogin).
      precisaEspaco: false,
      // A caixa para onde o disco desce nesse passo, entregue pelo mesmo
      // evento. Fora dele e null, que moverOrb() le como "de volta ao topo".
      // E um no do DOM, nao dado de tela: nenhum template o le.
      ancoraOrb: null,
      // O host das particulas, guardado na adocao. Tambem e no do DOM, e existe
      // por uma razao so: no beforeDestroy o getElementById nao acha mais ele
      // (ver devolverParticulas()).
      hostParticulas: null,
      // Quais links de "saiba mais" ja avisaram nesta passagem pela tela. NAO
      // e estado de tela - nenhum template o le -, entao nem precisaria ser
      // reativo; fica aqui porque o tempo de vida certo e o do componente, e
      // uma nova visita ao /login volta a avisar. Ver avisarSaibaMais().
      saibaMaisAvisados: new Set(),
    };
  },

  computed: {
    // Computed, e nao data: com $t() resolvido no data() os textos congelariam
    // no idioma do boot, e o seletor acima trocaria tudo na tela menos esta
    // lista.
    cards() {
      return [
        {
          // `id` nao e o titulo: o titulo vem do i18n e muda com o idioma, e o
          // alerta precisa de um nome que signifique a mesma coisa nas tres
          // linguas para dar para contar cliques por produto.
          id: "pricing",
          // Texto em uma string so, sem os descriptionOne..Four: aquele
          // fatiamento existia por quebra de linha manual, e a view junta tudo
          // com espaco logo em seguida.
          title: this.$t("landing.pricing.title"),
          desc: [this.$t("landing.pricing.description")],
          link: "https://quartix.io/products/pricing/",
        },
        {
          id: "insight",
          title: this.$t("landing.insight.title"),
          desc: [this.$t("landing.insight.description")],
          link: "https://quartix.io/products/analytics/",
        },
        {
          id: "distribution",
          title: this.$t("landing.distribution.title"),
          desc: [this.$t("landing.distribution.description")],
          link: "https://quartix.io/products/execution-and-distribution/",
        },
      ];
    },
    heroStyle() {
      if (!this.tuning) return {};
      return {
        "--veil": this.tune.veil,
        "--sat": this.tune.sat,
        "--bright": this.tune.bright,
      };
    },
    tuneSnippet() {
      const { veil, sat, bright, scale, drift } = this.tune;
      return [
        `--veil: ${veil.toFixed(2)};`,
        `--sat: ${sat.toFixed(2)};`,
        `--bright: ${bright.toFixed(2)};`,
        `data-particles-scale="${scale.toFixed(2)}"`,
        `data-drift="${drift.toFixed(2)}"`,
      ].join("\n");
    },
  },

  watch: {
    // Ha um caminho de escrita ao vivo porque particlesScale e lido a cada
    // frame; o slider so aparece com ?tune.
    "tune.scale"(valor) {
      const cena = document.getElementById("login-particles")?.__particlesScene;
      if (cena) cena.particlesScale = valor;
    },
    "tune.drift"(valor) {
      const cena = document.getElementById("login-particles")?.__particlesScene;
      if (cena) cena.drift = valor;
    },
  },

  mounted() {
    this.adotarParticulas();
  },

  beforeDestroy() {
    this.devolverParticulas();
  },

  methods: {
    trocarIdioma(lang) {
      setLocale(lang);
    },

    // Clique num dos tres links de produto: manda uma mensagem para o Telegram
    // dizendo QUEM (a marca de visitante e o retrato do navegador, o mesmo que
    // o press & hold coleta) foi ler sobre O QUE. Fora dela nao ha registro
    // nenhum: o link sai para o site institucional, que e outro dominio.
    //
    // Uma vez por link por passagem pela tela. O clique repetido no mesmo link
    // e quase sempre duplo-clique, e sao TRES mensagens no maximo - o balde do
    // nginx e de 6r/m com rajada de 3, entao sem isto uma pessoa impaciente
    // gastaria a cota e o alerta seguinte se perderia em silencio.
    //
    // `peekVisitor`, e nao `touchVisitor`: aqui nao ha tentativa de entrada
    // para contar (ver o comentario no visitor.js).
    //
    // Sem `await` e com o erro engolido: o `<a>` abre a outra aba na sequencia
    // deste handler, e a navegacao nao pode depender de uma API de e-mail. A
    // requisicao sobrevive - a aba de origem continua aqui, com target=_blank.
    avisarSaibaMais(card) {
      if (this.saibaMaisAvisados.has(card.id)) return;
      this.saibaMaisAvisados.add(card.id);

      sendLearnMoreAlert({
        // A mensagem leva o mesmo titulo dos tres passos do login - e atividade
        // da mesma tela -, entao quem a distingue no chat e este `action`,
        // exatamente como entre os passos.
        action: "learn_more",
        product: card.id,
        link: card.link,
        locale: this.$i18n.locale,
        origin: this.$route ? this.$route.fullPath : "",
        ...peekVisitor(),
        ...collectClientInfo(),
      }).catch(() => {});
    },

    ajustarEspaco(precisa, ancora) {
      if (this.precisaEspaco === precisa) return;
      this.precisaEspaco = precisa;
      this.ancoraOrb = precisa ? ancora || null : null;
      this.moverOrb();
      this.ajustarOrb();
    },

    // O disco troca de vizinho conforme o passo: nos de login ele abre o bloco,
    // no do "request a demo" desce para logo abaixo do titulo, na ancora que o
    // CarouselModalLogin manda junto do evento.
    //
    // Quem se move e o proprio .panel-orb, e nao uma segunda caixa que
    // recebesse o host das particulas. Duas razoes: o diametro, a mascara e o
    // fade continuam declarados num arquivo so - e mover no DOM preserva o
    // contexto WebGL, enquanto recriar a caixa perderia o canvas ja compilado.
    //
    // Vue nao desfaz isto: o .panel-orb nao tem v-if nem v-for, entao o patch
    // do .panel-body so atualiza a classe `is-live` no lugar em que o elemento
    // estiver. A volta ao topo tambem e manual, por isso o insertBefore.
    moverOrb() {
      const orb = this.$refs.particles;
      const destino = this.ancoraOrb || this.$refs.corpo;
      if (!orb || !destino || orb.parentNode === destino) return;

      if (destino === this.$refs.corpo) {
        destino.insertBefore(orb, destino.firstChild);
      } else {
        destino.appendChild(orb);
      }
    },

    // O disco encolhe no passo do formulario para devolver altura ao painel.
    // Duas coisas nao acontecem sozinhas quando a caixa muda de tamanho:
    //
    // 1. O canvas so rele `container.offsetWidth` dentro de onWindowResize() do
    //    modulo - sem o evento sintetico ele fica no tamanho antigo, esticado
    //    pelo CSS. Por isso tambem NAO ha transicao de tamanho no CSS: o canvas
    //    nao acompanharia a animacao, so daria um salto no fim.
    // 2. O tamanho de cada particula sai de `largura_do_canvas / 2000 * escala`,
    //    entao encolher a caixa encolhe a particula junto - e demais. A escala
    //    do host (data-particles-scale, no index.html) foi escolhida para o
    //    disco grande; no disco pequeno ela sobe por ORB_FATOR_COMPACTO, que e
    //    uma compensacao PARCIAL de proposito (ver a constante).
    ajustarOrb() {
      const host = document.getElementById("login-particles");
      const cena = host?.__particlesScene;
      if (!cena) return;

      const base = parseFloat(host.getAttribute("data-particles-scale") || "2.2");
      cena.particlesScale = this.precisaEspaco ? base * ORB_FATOR_COMPACTO : base;

      // nextTick espera a classe entrar no DOM; o rAF espera o layout fechar,
      // que e quando offsetWidth ja vale o tamanho novo. Mesma sequencia de
      // adotarParticulas().
      this.$nextTick(() =>
        requestAnimationFrame(() => window.dispatchEvent(new Event("resize")))
      );
    },

    // Adota o host criado no index.html (ver o comentario la). Ele ja foi
    // construido pelo modulo la fora da viewport; aqui so muda de lugar no DOM,
    // o que preserva o contexto WebGL.
    adotarParticulas() {
      const host = document.getElementById("login-particles");
      if (!host || !this.$refs.particles) return;

      // A referencia fica guardada porque na saida da rota a busca por id nao
      // encontra mais nada - ver devolverParticulas().
      this.hostParticulas = host;
      this.$refs.particles.appendChild(host);
      host.classList.add("is-adopted");

      // O canvas foi dimensionado no boot, quando o host media a viewport
      // inteira - esta coluna e mais estreita. onWindowResize() do modulo
      // rele o offsetWidth do container, entao o evento sintetico e o que
      // acerta o tamanho. nextTick espera o DOM; o rAF espera o layout fechar.
      this.$nextTick(() =>
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event("resize"));
          this.recentralizar();
          // A cena sobrevive a esta view - o host so muda de lugar no DOM -,
          // entao a escala pode ter ficado no valor compacto de uma visita
          // anterior que terminou no passo do formulario.
          this.ajustarOrb();
          this.particulasVivas = true;
        })
      );
    },

    // Devolver ao body e obrigatorio: sem isso o host sai do documento junto
    // com esta view e o proximo /login (depois de um logout) nao encontra mais
    // nada para adotar. Fora do documento ele tambem para de renderizar, porque
    // o IntersectionObserver interno deixa de ver intersecao.
    //
    // O host vem da referencia guardada, NAO de document.getElementById: o Vue 2
    // tira o elemento da view do documento ANTES de chamar o beforeDestroy
    // (`removeVnodes` chama `removeAndInvokeRemoveHook` e so depois
    // `invokeDestroyHook`, que e quem dispara o hook). Quando este metodo roda,
    // o .panel-orb - e o host dentro dele - ja estao destacados, e uma busca
    // por id no documento devolve null. Era o que perdia o host para sempre: o
    // /login seguinte nao achava nada para adotar e aparecia sem particulas ate
    // um F5. `appendChild` reata elemento destacado, e o contexto WebGL
    // sobrevive a isso do mesmo jeito que sobrevive a adocao.
    devolverParticulas() {
      const host = this.hostParticulas || document.getElementById("login-particles");
      if (!host) return;
      host.classList.remove("is-adopted");
      document.body.appendChild(host);
      this.hostParticulas = null;
    },

    // (0,0) e o centro do plano de particulas, que a camera enquadra no centro
    // do container. Com data-interactive="false" o anel nunca persegue o
    // cursor: ele so oscila em torno desse ponto pelo ruido, entao centralizar
    // uma vez basta para ele ficar ancorado no meio da area.
    // `__particlesScene` so existe por causa do patch em
    // MainParticlesComponent.js; se ele sumir, isto vira no-op silencioso.
    recentralizar() {
      const cena = document.getElementById("login-particles")?.__particlesScene;
      if (!cena?.particles) return;
      cena.particles.ringPos.set(0, 0);
      cena.particles.cursorPos.set(0, 0);
    },
  },
};
</script>

<style scoped>
.login-page {
  flex: 1;
  display: flex;
  min-height: 0;
  background: #000;
}

/* ------------------------------------------------------------------ arte  */
/*
  A imagem e a torre de vidro, em preto e branco, sob um veu chapado. Tres
  numeros governam o tratamento - mexa neles, nao no resto do bloco:

    --veil    opacidade do veu escuro sobre a imagem (0 = imagem crua)
    --sat     saturacao que sobra da imagem (1 = crua, 0 = cinza)
    --bright  brilho da imagem antes do veu

  Para calibrar na tela real abra /login?tune.
*/
.hero {
  --veil: 0.0;
  --sat: 0;
  --bright: 1;

  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  background: #000;
}

.hero-art {
  position: absolute;
  inset: 0;
  /* 65% na horizontal, nao centro: a imagem e 2:1 e a coluna e quase quadrada,
     entao o cover corta quase metade da largura. Centralizado sobraria ceu
     vazio; deslocado para a direita entra a curva de vidro. */
  background: url("@/assets/glasstower3.png") no-repeat 65% 50% / cover;
  /* --sat: 0 e o preto e branco (saturate(0) e a mesma matriz de grayscale(1)),
     e continua sendo uma variavel, entao da para devolver a cor no ?tune sem
     mexer em codigo. */
  //filter: saturate(var(--sat)) brightness(var(--bright));
}

/* Veu chapado: --veil e a opacidade final, sem gradiente no meio do caminho.
   A costura com o painel vira uma aresta vertical nitida - o que e o mesmo que
   a referencia faz, onde a foto simplesmente termina e o painel comeca. */
.hero-veil {
  position: absolute;
  inset: 0;
  background: #000;
  opacity: var(--veil);
}

.hero-tuner {
  position: absolute;
  left: 1.5rem;
  bottom: 1.5rem;
  width: 300px;
  padding: 0.9rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(6px);
  font-size: 12px;
  color: #e6e7ea;
}
.tuner-title {
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #ffbf00;
  margin-bottom: 0.4rem;
}
.tuner-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.tuner-label {
  width: 74px;
  color: #9aa3af;
}
.tuner-slider {
  flex: 1;
  margin: 0;
  padding: 0;
}
.tuner-value {
  width: 34px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.tuner-snippet {
  margin: 0.6rem 0 0;
  padding: 0.5rem;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.06);
  font-size: 11px;
  line-height: 1.5;
  color: #c7c9ce;
}

/* ---------------------------------------------------------------- painel  */
.panel {
  /* Um terco da viewport, sem teto. O clamp anterior tinha maximo de 600px, e
     era ele que fazia a coluna encolher para ~25% em monitor largo - a
     porcentagem nem chegava a valer. Hoje sao 50/50, a proporcao da
     referencia. O minimo continua, para a coluna nao espremer o formulario em
     tela estreita. */
  --faixa: 430px;

  flex: 0 0 50%;
  min-width: 420px;
  display: flex;
  flex-direction: column;
  padding: 2.5rem clamp(2rem, 4vw, 4rem);
  background: #000;
  overflow-y: auto;
  scrollbar-width: none;
}

/* A coluna e larga o bastante para o conteudo se perder esticado de ponta a
   ponta, entao tudo vive numa faixa de largura fixa, centralizada - disco,
   formulario, links e rodape no mesmo eixo e na mesma largura.
   --faixa e a unica fonte dessa medida: o botao do formulario e block, entao
   ele mede a faixa inteira, e a lista de produtos idem. Um teto proprio em
   qualquer um dos dois quebra o alinhamento em silencio - foi o que acontecia
   com o max-width de 400px que o formulario tinha. */
.panel > * {
  width: 100%;
  max-width: var(--faixa);
  margin-left: auto;
  margin-right: auto;
}

/* O logo e a excecao a faixa centralizada: ele ancora no canto, junto do
   padding esquerdo do painel, e nao no eixo do formulario. */
.panel-head {
  flex: 0 0 auto;
  max-width: none;
  margin-left: 0;
  margin-right: 0;
  /* Logo num canto, idioma no outro: como este bloco escapa da faixa, os dois
     ancoram no padding do painel. */
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.panel-logo {
  display: block;
  width: 132px;
  height: auto;
}

/* Discreto de proposito: o mesmo cinza e o mesmo corpo do rodape, que e o outro
   texto de servico da tela. So o idioma corrente ganha o ambar da marca - sem
   caixa, borda ou fundo, que competiriam com o formulario. */
.lang-switch {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.lang-option {
  padding: 0.15rem 0;
  border: 0;
  background: none;
  /* button nao herda familia do documento em nenhum navegador. */
  font-family: inherit;
  font-size: 0.72rem;
  letter-spacing: 0.12em;
  color: #6f7480;
  cursor: pointer;
  transition: color 0.2s ease;
}
.lang-option + .lang-option {
  padding-left: 0.5rem;
  border-left: 1px solid rgba(255, 255, 255, 0.18);
}
.lang-option:hover {
  color: #c7c9ce;
}
.lang-option.is-active {
  color: #ffbf00;
}
/* :focus-visible, nao :focus - o clique com mouse nao deve deixar anel aceso. */
.lang-option:focus {
  outline: none;
}
.lang-option:focus-visible {
  outline: 1px solid rgba(255, 255, 255, 0.35);
  outline-offset: 3px;
}

/* O login fica no bloco elastico do meio: centralizado na tela alta, colado
   no logo na tela baixa, sem nunca empurrar o rodape para fora. */
.panel-body {
  /* `0` no shrink, e nao o `1` de sempre: este e o unico item elastico da
     coluna, entao numa viewport baixa era ele que era espremido - e com o
     min-height: 0 que morava aqui ele encolhia ABAIXO do proprio conteudo, que
     entao transbordava POR CIMA da lista de produtos (nao e z-index: o bloco
     colapsa e o conteudo escapa da caixa). Sem encolhimento a sobra vira
     rolagem do .panel, que e o comportamento certo - sobreposicao nunca pode
     ser o modo de falha. Chegar na rolagem, porem, ja e ruim: quem evita isso
     sao as duas media queries de altura, no fim da folha. */
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  /* align-items center, nao flex-start: o disco tem largura propria e precisa
     cair no mesmo eixo dos campos, que ocupam a faixa inteira. */
  align-items: center;
  /* No eixo vertical a centralizacao vem de margem auto, e NAO de
     justify-content: center. Os dois centralizam igual enquanto o conteudo
     cabe, mas quando ele nao cabe - o passo "request a demo" e um formulario
     inteiro - o justify-content empurra a sobra para os dois lados e a parte de
     cima fica inalcancavel: a rolagem so chega na sobra de baixo. Margem auto
     resolve para zero quando nao ha espaco sobrando, entao nao esconde nada. */
  justify-content: flex-start;
  padding: 2rem 0;
}
.panel-body > :first-child {
  margin-top: auto;
}
.panel-body > :last-child {
  margin-bottom: auto;
}

/* O disco de particulas. Quadrado de proposito: o campo e enquadrado pela
   camera em cima do container, entao a area define o diametro - nao ha raio
   para ajustar em lugar nenhum. Encolher aqui encolhe o anel.
   A mascara e o que faz ler como disco; sem ela o campo termina num quadrado
   de bordas duras. E a opacidade so sobe depois do primeiro frame, que e onde
   o WebGL compila os shaders. */
.panel-orb {
  position: relative;
  flex: 0 0 auto;
  width: 190px;
  height: 190px;
  margin-bottom: 0.5rem;
  opacity: 0;
  transition: opacity 0.9s ease;
  -webkit-mask-image: radial-gradient(
    closest-side circle at center,
    #000 0%,
    #000 52%,
    transparent 92%
  );
  mask-image: radial-gradient(
    closest-side circle at center,
    #000 0%,
    #000 52%,
    transparent 92%
  );
}
.panel-orb.is-live {
  opacity: 1;
}

/* Passo do "request a demo": o formulario inteiro nao cabe, entao o disco cede
   altura. Note que a transicao declarada acima e SO de opacidade - o canvas nao
   acompanha animacao de tamanho (ele so rele o container num evento de resize),
   entao a caixa muda de uma vez. O tamanho da particula e compensado em JS, em
   ajustarOrb(); os 80px aqui e a constante ORB_PX_COMPACTO la sao o mesmo
   numero. */
.panel-body.is-compact .panel-orb {
  width: 80px;
  height: 80px;
}
.panel-body.is-compact {
  padding: 0.75rem 0;
  /* O flex-shrink: 0 que morava aqui subiu para o .panel-body: o transbordo
     por cima dos irmaos nao era exclusividade do formulario de demo - qualquer
     tela baixa o produz. Ver o comentario la em cima. */
}

/* Quem pediu menos movimento fica so com o formulario. */
@media (prefers-reduced-motion: reduce) {
  .panel-orb {
    display: none;
  }
  /* Sem o disco, quem passa a carregar a margem de cima e o formulario. */
  .panel-body > :nth-child(2) {
    margin-top: auto;
  }
}

.panel-links {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.panel-foot {
  flex: 0 0 auto;
  padding-top: 1.5rem;
  /* A faixa ja e centralizada no painel, entao centralizar o texto dentro dela
     centraliza no painel inteiro. */
  text-align: center;
  font-size: 0.72rem;
  color: #6f7480;
}

/* Abaixo de 960 a coluna dupla nao cabe e a arte sai de cena.
   Ela ja foi uma faixa de 160px no topo - alta demais para o recorte da torre
   se ler como imagem, baixa demais para valer a altura que tirava do
   formulario: no celular ela aparecia como um retangulo cinza sem assunto,
   empurrando o login para baixo da dobra. Quem carrega a marca aqui e o logo
   com o disco de particulas, e por isso o disco cresce (abaixo).

   display:none e nao v-if de proposito: alem de responder a rotacao sem
   remontar nada, o navegador nao busca o background-image de uma subarvore
   escondida - o .webp da torre deixa de ser baixado no celular. O tuner mora
   dentro do .hero, entao some junto. */
@media (max-width: 960px) {
  .login-page {
    flex-direction: column;
    overflow-y: auto;
  }
  .hero {
    display: none;
  }
  /* Sem a arte sobra altura, e o disco e a unica imagem da tela: ele deixa de
     ser os 150px de antes e passa a sair do diametro do desktop (ORB_PX). O
     min() e o teto para o celular estreito, onde o diametro cheio comeria a
     largura util - a 45vw ele acompanha a tela e so encosta no teto a partir
     de ~420px de largura.

     Os 0.85 sao um -15% no diametro pedido na tela de celular: o disco cheio
     ficou grande demais ali. O fator multiplica o min() inteiro de proposito -
     os dois limites encolhem juntos, e continua havendo UM numero de diametro
     nesta folha.

     Nao ha compensacao em JS aqui, ao contrario do disco compacto do "request
     a demo" (ver ajustarOrb e ORB_FATOR_COMPACTO): o tamanho da particula sai
     da largura do canvas, entao a nuvem encolhe 15% junto com a caixa - que e
     exatamente a reducao fiel que se quer. A compensacao la existe porque
     aquela reducao e de 58%, onde a particula viraria poeira. */
  .panel-orb {
    --orb-mobile: min(190px, 45vw);
    width: calc(var(--orb-mobile) * 0.85);
    height: calc(var(--orb-mobile) * 0.85);
  }
  .panel {
    flex: 1 1 auto;
    /* O min-width de 420px estouraria a tela num celular de 375px. */
    min-width: 0;
    padding: 2rem 1.5rem;
  }
  .panel-body {
    padding: 2rem 0;
  }
  /* Alvo de toque: na tela do dedo os tres idiomas sao dois caracteres com
     0,15rem de folga, ou seja ~17px de altura num alvo que a recomendacao poe
     em 44. O texto continua do mesmo tamanho - o que cresce e a area
     clicavel. */
  .lang-option {
    padding: 0.7rem 0.4rem;
  }
  .lang-option + .lang-option {
    padding-left: 0.7rem;
  }
}

/* ------------------------------------------------ tela baixa (altura)  */
/* O painel e uma pilha de alturas fixas - logo, formulario, produtos, rodape -
   e a soma delas nao cabe abaixo de ~820px de viewport. Como o .panel-body nao
   encolhe mais (acima), o que sobra vira rolagem; a rolagem, porem, joga o
   botao LOG IN para fora da dobra, que e o oposto do que a pagina quer. Entao
   a altura tem duas faixas, e as duas ficam DEPOIS da media query de largura
   de proposito: quando as duas valem (celular deitado), quem manda e a altura.

   1. <= 880px: aperta o ritmo vertical. Recupera ~110px, o bastante para uma
      tela de ~760px continuar mostrando tudo.
   2. <= 780px: os produtos saem de cena. Nem o aperto basta aqui, e a escolha
      entre esconder o secundario ou empurrar o login para baixo da dobra e
      facil: o assunto da pagina e entrar, a lista de produtos e vitrine.
      display: none e nao v-if - some do fluxo do flex (as margens auto do
      .panel-body voltam a centralizar o que ficou), responde ao resize sem
      passar por JS e nao concorre com o v-if do passo do "request a demo",
      que esconde a mesma lista por outro motivo. */
@media (max-height: 880px) {
  .panel {
    padding-top: 2rem;
    padding-bottom: 2rem;
  }
  /* Sem compensacao em JS, pela mesma razao do disco do celular: o tamanho da
     particula sai da largura do canvas, entao a nuvem encolhe 21% junto com a
     caixa - reducao fiel. (A compensacao do .is-compact existe porque la a
     reducao e de 58%, onde a particula viraria poeira.)
     O min() repete o teto do celular para o caso das duas media queries
     valerem juntas: esta vem depois e ganharia, e sem o teto o disco voltaria
     a comer a largura util de um aparelho estreito. */
  .panel-orb {
    --orb-baixo: min(150px, 45vw);
    width: var(--orb-baixo);
    height: var(--orb-baixo);
  }
  .panel-body {
    padding: 1rem 0;
  }
  .panel-foot {
    padding-top: 1rem;
  }
}

@media (max-height: 780px) {
  .panel-links {
    display: none;
  }
}
</style>
